import React, { useState, useEffect } from 'react';
import api from '../config';
import { Loader2, TrendingUp, Plus, Rocket, Factory, AlertTriangle } from 'lucide-react';

/**
 * Operating model — the value-creation build-up behind the LBO model.
 *
 * The LBO answers "what return at this price?"; this answers "how does EBITDA
 * grow from here, and which lever produces it?" across Organic, M&A (add-ons)
 * and De Novo (new sites). Every number is computed server-side strictly from
 * the assumptions entered here — nothing is estimated or invented. A lever
 * switched on without its inputs comes back as a gap, not a guess.
 */

const fmtUSD = (v) => (v == null || v === '' ? '—'
  : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtM = (v) => (v == null || v === '' ? '—'
  : `$${(Number(v) / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`);
const pct = (v) => (v == null || v === '' ? '—' : `${Number(v).toFixed(1)}%`);

function Field({ label, value, onChange, hint, req, pref }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}{req && <span className="text-red-500 ml-0.5">*</span>}
        {pref && (
          <span className="ml-1.5 inline-block whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px align-middle"
            title="Prefilled from this deal's intake details — edit freely">from deal</span>
        )}
      </span>
      <input type="number" step="any" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        className="form-input mt-0.5" />
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function Lever({ title, icon: Icon, on, onToggle, children, note }) {
  return (
    <div className={`rounded-lg border overflow-hidden ${on ? 'border-brand-300 bg-white' : 'border-slate-200 bg-slate-50'}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-100/70">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Icon className="w-4 h-4 text-slate-500" />{title}
        </span>
        <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${on ? 'bg-brand-100 text-navy-900' : 'bg-slate-200 text-slate-500'}`}>
          {on ? 'on' : 'off'}
        </span>
      </button>
      {on && <div className="p-4 pt-1 space-y-3">{children}</div>}
      {!on && note && <p className="px-4 pb-3 text-[11px] text-slate-400">{note}</p>}
    </div>
  );
}

function SummaryTile({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-navy-900">{value}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

export default function OperatingModel({ dealId, deal }) {
  const [inputs, setInputs] = useState(null);
  const [model, setModel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/dd/deals/${dealId}/operating-model`)
      .then(res => {
        if (cancelled) return;
        setInputs(res.data.inputs);
        setModel(res.data.model);
        setSavedAt(res.data.updated_at);
      })
      .catch(() => api.get(`/api/dd/deals/${dealId}/operating-model/defaults`)
        .then(res => { if (!cancelled) setInputs(res.data.inputs); })
        .catch(() => { if (!cancelled) setErr('Could not load the model inputs.'); }));
    return () => { cancelled = true; };
  }, [dealId]);

  const set = (path, value) => setInputs(prev => {
    const next = { ...prev };
    if (path.length === 1) next[path[0]] = value;
    else next[path[0]] = { ...next[path[0]], [path[1]]: value };
    return next;
  });

  const build = async () => {
    setBusy(true); setErr('');
    try {
      const res = await api.post(`/api/dd/deals/${dealId}/operating-model`, inputs);
      setModel(res.data.model);
      setSavedAt(res.data.updated_at);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'The model could not be built — check the inputs.');
    } finally {
      setBusy(false);
    }
  };

  if (!inputs) {
    return (
      <div className="card p-6 flex items-center justify-center gap-2 text-sm text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the operating model…
      </div>
    );
  }

  const s = model?.summary;
  const gaps = model?.gaps || [];

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-navy-900">How EBITDA grows from here</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Build the value-creation plan across three levers. Caply computes the yearly bridge from your
            assumptions only — a lever left on without its inputs is reported as a gap, never guessed.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Years" value={inputs.years} onChange={v => set(['years'], v)} req />
          <Field label="Today's revenue ($)" value={inputs.base_revenue} onChange={v => set(['base_revenue'], v)} req pref />
          <Field label="Today's EBITDA margin (%)" value={inputs.base_ebitda_margin_pct} onChange={v => set(['base_ebitda_margin_pct'], v)} req pref />
          <Field label="Exit multiple (x EBITDA)" value={inputs.exit_multiple} onChange={v => set(['exit_multiple'], v)} hint="optional — prices the exit" pref />
        </div>

        <Lever title="Organic growth" icon={TrendingUp} on onToggle={() => {}}
          note="Always on — the base business compounds.">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Revenue growth (% a year)" value={inputs.organic.revenue_growth_pct} onChange={v => set(['organic', 'revenue_growth_pct'], v)} req />
            <Field label="Margin expansion (bps a year)" value={inputs.organic.margin_expansion_bps_per_year} onChange={v => set(['organic', 'margin_expansion_bps_per_year'], v)} hint="100 bps = 1.0%" />
          </div>
        </Lever>

        <Lever title="M&A — add-ons" icon={Plus} on={inputs.ma.enabled} onToggle={() => set(['ma', 'enabled'], !inputs.ma.enabled)}
          note="Off — add-ons are not part of this plan.">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Add-ons a year" value={inputs.ma.addons_per_year} onChange={v => set(['ma', 'addons_per_year'], v)} req />
            <Field label="First year" value={inputs.ma.first_year} onChange={v => set(['ma', 'first_year'], v)} />
            <Field label="Years of add-ons" value={inputs.ma.years_of_addons} onChange={v => set(['ma', 'years_of_addons'], v)} />
            <Field label="Revenue per add-on ($)" value={inputs.ma.revenue_per_addon} onChange={v => set(['ma', 'revenue_per_addon'], v)} req />
            <Field label="Add-on EBITDA margin (%)" value={inputs.ma.ebitda_margin_pct} onChange={v => set(['ma', 'ebitda_margin_pct'], v)} req />
            <Field label="Synergies (% of add-on revenue)" value={inputs.ma.synergy_pct_of_revenue} onChange={v => set(['ma', 'synergy_pct_of_revenue'], v)} hint="phases in over 2 years" />
            <Field label="Entry multiple paid (x EBITDA)" value={inputs.ma.entry_multiple} onChange={v => set(['ma', 'entry_multiple'], v)} hint="sizes the capital deployed" />
          </div>
        </Lever>

        <Lever title="De Novo — new sites / builds" icon={Factory} on={inputs.de_novo.enabled} onToggle={() => set(['de_novo', 'enabled'], !inputs.de_novo.enabled)}
          note="Off — no greenfield builds are part of this plan.">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Sites a year" value={inputs.de_novo.sites_per_year} onChange={v => set(['de_novo', 'sites_per_year'], v)} req />
            <Field label="First year" value={inputs.de_novo.first_year} onChange={v => set(['de_novo', 'first_year'], v)} />
            <Field label="Years of builds" value={inputs.de_novo.years_of_builds} onChange={v => set(['de_novo', 'years_of_builds'], v)} />
            <Field label="Revenue per site at maturity ($)" value={inputs.de_novo.revenue_per_site_at_maturity} onChange={v => set(['de_novo', 'revenue_per_site_at_maturity'], v)} req />
            <Field label="Ramp (years to maturity)" value={inputs.de_novo.ramp_years} onChange={v => set(['de_novo', 'ramp_years'], v)} />
            <Field label="EBITDA margin at maturity (%)" value={inputs.de_novo.ebitda_margin_pct} onChange={v => set(['de_novo', 'ebitda_margin_pct'], v)} req />
            <Field label="Capex per site ($)" value={inputs.de_novo.capex_per_site} onChange={v => set(['de_novo', 'capex_per_site'], v)} />
          </div>
        </Lever>

        <div className="flex items-center gap-3">
          <button onClick={build} disabled={busy} className="btn-primary inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            {busy ? 'Building…' : 'Build the model'}
          </button>
          {savedAt && !busy && <span className="text-[11px] text-slate-400">Last built {savedAt.replace('T', ' ').replace('Z', ' UTC')}</span>}
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
      </div>

      {model && (
        <>
          {gaps.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <p className="font-semibold">Still needed before this lever counts:</p>
                <ul className="list-disc ml-4 mt-0.5">{gaps.map(g => <li key={g}>{g}</li>)}</ul>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryTile label="Exit revenue" value={fmtM(s.exit_revenue)} sub={`${pct(s.revenue_cagr_pct)} CAGR`} />
            <SummaryTile label="Exit EBITDA" value={fmtM(s.exit_ebitda)} sub={`${pct(s.exit_margin_pct)} margin`} />
            <SummaryTile label="EBITDA added" value={fmtM(s.ebitda_added)} sub={`organic ${fmtM(s.organic_ebitda_added)} · M&A ${fmtM(s.ma_ebitda_added)} · de novo ${fmtM(s.de_novo_ebitda_added)}`} />
            <SummaryTile label="Capital deployed" value={fmtM(s.capital_deployed)} sub={s.value_multiple_on_capital != null ? `${s.value_multiple_on_capital}x value on capital` : 'no capital in this plan'} />
          </div>

          <div className="card p-5">
            <h3 className="text-sm font-semibold text-navy-900 mb-3">The yearly bridge</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-200">
                    <th className="py-2">Year</th>
                    <th className="py-2 text-right">Organic</th>
                    <th className="py-2 text-right">M&A</th>
                    <th className="py-2 text-right">De Novo</th>
                    <th className="py-2 text-right">Revenue</th>
                    <th className="py-2 text-right">EBITDA</th>
                    <th className="py-2 text-right">Margin</th>
                    <th className="py-2 text-right">Capital</th>
                  </tr>
                </thead>
                <tbody>
                  {model.rows.map(r => (
                    <tr key={r.year} className="border-b border-slate-100">
                      <td className="py-2 font-medium text-slate-700">Year {r.year}</td>
                      <td className="py-2 text-right text-slate-600">{fmtM(r.organic_revenue)}</td>
                      <td className="py-2 text-right text-slate-600">{r.ma_revenue ? fmtM(r.ma_revenue) : '—'}</td>
                      <td className="py-2 text-right text-slate-600">{r.de_novo_revenue ? fmtM(r.de_novo_revenue) : '—'}</td>
                      <td className="py-2 text-right font-medium text-navy-900">{fmtM(r.total_revenue)}</td>
                      <td className="py-2 text-right font-medium text-navy-900">{fmtM(r.total_ebitda)}</td>
                      <td className="py-2 text-right text-slate-600">{pct(r.ebitda_margin_pct)}</td>
                      <td className="py-2 text-right text-slate-600">{r.capital_deployed ? fmtM(r.capital_deployed) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">
              EBITDA by lever — organic {pct(s.organic_share_pct)}, M&A {pct(s.ma_share_pct)}, de novo {pct(s.de_novo_share_pct)} of the growth.
              {s.exit_value != null && ` At ${fmtUSD(s.exit_value)} of exit value, the plan creates ${fmtM(s.value_created)} net of ${fmtM(s.capital_deployed)} deployed.`}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
